// The ⋯ actions menu at the end of a list-table row. The menu is teleported to
// <body> and positioned against its trigger, so the table card's horizontal
// scroll doesn't clip it. It sits inside the row's x-data, so menu items can
// still pass the row (`row: $data`) to the status dialogs; anything that used
// $el.closest('tr') reaches the row through $refs.trigger instead.
//
// Deliberately defines nothing called active, status or archived: those are
// the row's, and the dialogs write them back through the merged scope.
export default () => ({
  open: false,

  toggle() {
    this.open ? this.close() : this.show();
  },

  show() {
    this.open = true;
    this.$nextTick(() => {
      this.place();
      this.items()[0]?.focus();
    });
  },

  close(returnFocus = false) {
    if (!this.open) {
      return;
    }

    this.open = false;

    if (returnFocus) {
      this.$refs.trigger.focus();
    }
  },

  // Visible ones only: some items hide themselves (x-show), e.g. "Make admin".
  items() {
    return [...this.$refs.menu.querySelectorAll('[role="menuitem"]')].filter((item) => item.getClientRects().length);
  },

  // Below the trigger, right-aligned; above it when there isn't room.
  place() {
    const trigger = this.$refs.trigger.getBoundingClientRect();
    const menu = this.$refs.menu;
    const height = menu.offsetHeight;
    const below = trigger.bottom + 4 + height <= window.innerHeight;

    menu.style.top = `${below ? trigger.bottom + 4 : trigger.top - 4 - height}px`;
    menu.style.left = `${Math.max(8, trigger.right - menu.offsetWidth)}px`;
  },

  onOutsideClick(event) {
    if (!this.$refs.trigger.contains(event.target)) {
      this.close();
    }
  },

  onKeydown(event) {
    const items = this.items();
    const index = items.indexOf(document.activeElement);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        items[(index + 1) % items.length]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        items[(index - 1 + items.length) % items.length]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        items[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        items[items.length - 1]?.focus();
        break;
      case 'Escape':
        event.preventDefault();
        this.close(true);
        break;
      case 'Tab':
        this.close();
        break;
      default:
    }
  },
});

// Clicking anywhere on a list row opens it, via the row's .row-link (so
// Cmd/Ctrl-click still opens a new tab). Clicks on the row's own controls and
// text selections are left alone.
export function enhanceRowLinks() {
  document.addEventListener('click', (event) => {
    const row = event.target.closest('.data-table tbody tr');

    if (!row || event.target.closest('a, button, input, select, textarea, label, [role="menu"]')) {
      return;
    }

    if (window.getSelection()?.toString()) {
      return;
    }

    const link = row.querySelector('a.row-link');

    if (!link) {
      return;
    }

    if (event.metaKey || event.ctrlKey) {
      window.open(link.href, '_blank', 'noopener');
    } else {
      link.click();
    }
  });
}
